import Articles from "articles";
import connectToStores from "alt-utils/lib/connectToStores";
import filter from "lodash/filter";
import get from "lodash/get";
import invoke from "lodash/invoke";
import trim from "lodash/trim";
import moment from "moment";
import PropTypes from "prop-types";
import React, { Component, createRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";

import Analytics from "../../utils/chat/Analytics";
import AskFormStore from "../../stores/chat/AskFormStore";
import AskQuestionFormButton from "./AskQuestionFormButton";
import AskQuestionFormNameInput from "./AskQuestionFormNameInput";
import AskQuestionFormQuestionInput from "./AskQuestionFormQuestionInput";
import AskQuestionFormContactInfoInput from "./AskQuestionFormContactInfoInput";
import AuthStore from "../../stores/chat/AuthStore";
import { PostMessage } from "../../constants/chat";
import ChatActions from "../../actions/ChatActions";
import ConfigStore from "../../stores/chat/ConfigStore";
import { EVENT_ACTIONS } from "../../constants/AnalyticsConstants";
import i18next from "../../utils/i18next";
import { isMobileDevice, isNodeInRoot, postMessageToParent } from "../../utils/Utils";

export class AskQuestionForm extends Component {
    constructor({ showDisclaimer }) {
        super();

        this.state = {
            agreedWithDisclaimer: !showDisclaimer,
            formWasSubmitted: false,
            recaptchaValue: null,
        };

        this.onAgreeWithDisclaimer = this.onAgreeWithDisclaimer.bind(this);
        this.onFormSubmit = this.onFormSubmit.bind(this);
        this.onRecaptchaChange = this.onRecaptchaChange.bind(this);

        this.focusFirstErroredInput = this.focusFirstErroredInput.bind(this);
        this.focusFirstInput = this.focusFirstInput.bind(this);
        this.preventScrollingOutsideOfTextarea = this.preventScrollingOutsideOfTextarea.bind(this);
        this.setInputRef = this.setInputRef.bind(this);

        this.questionFormRef = createRef();
        this.questionButtonRef = createRef();
        this.inputRefs = [];

        ChatActions.resetChat();
    }

    componentDidMount() {
        ChatActions.loadLastResponseTime(this.props.configId);

        if (!this.props.isLoading) {
            if (!isMobileDevice()) {
                this.focusFirstInput();
            }

            Analytics.sendGaEvent({ action: EVENT_ACTIONS.NewQuestionAsked });
            /*
                For whatever reason, Firefox needs more time until we can get
                askForm's height, that's why the 'setTimeout()'
            */
            setTimeout(() => {
                postMessageToParent(PostMessage.SET_HEIGHT, {
                    height: this.askFormHeight,
                });
            }, 20);
            document.addEventListener("touchmove", this.preventScrollingOutsideOfTextarea);
        }
    }

    componentWillReceiveProps({ isLoading, showDisclaimer }) {
        const hasFinishedLoading = this.props.isLoading === true && isLoading === false;

        if (hasFinishedLoading) {
            this.setState(() => ({ agreedWithDisclaimer: !showDisclaimer }));
        }
    }

    componentDidUpdate(prevProps) {
        const hasFinishedLoading = prevProps.isLoading === true && this.props.isLoading === false;
        const hasLoadedLastResponseTime = prevProps.lastResponseTimeInMillis !== this.props.lastResponseTimeInMillis;

        if (hasFinishedLoading || hasLoadedLastResponseTime) {
            if (!isMobileDevice()) {
                this.focusFirstInput();
            }
            setTimeout(() => {
                postMessageToParent(PostMessage.SET_HEIGHT, {
                    height: this.askFormHeight,
                });
            }, 20);
            document.addEventListener("touchmove", this.preventScrollingOutsideOfTextarea);
        }

        if (prevProps.showDisclaimer !== this.props.showDisclaimer) {
            setTimeout(() => {
                postMessageToParent(PostMessage.SET_HEIGHT, {
                    height: this.askFormHeight,
                });
            }, 20);
        }
    }

    componentWillUnmount() {
        postMessageToParent(PostMessage.RESET_HEIGHT);
        document.removeEventListener("touchmove", this.preventScrollingOutsideOfTextarea);
    }

    static getStores() {
        return [AskFormStore, AuthStore, ConfigStore];
    }

    static getPropsFromStores() {
        const {
            customTags,
            id: configId,
            labels,
            metadata,
            questionCategory,
            showDisclaimer,
        } = ConfigStore.getConfig();
        const { isLoading } = AuthStore.getState();
        const {
            contactInfo,
            error,
            isProcessing,
            lastResponseTimeInMillis,
            questionText,
            userName,
        } = AskFormStore.getState();

        return {
            configId,
            contactInfo,
            contactInfoIsValid: AskFormStore.isContactInfoValid(),
            contactInfoWasPrefilled: AskFormStore.contactInfoWasPrefilled(),
            customTags,
            error,
            isLoading,
            isProcessing,
            labels,
            lastResponseTimeInMillis,
            metadata,
            questionCategory,
            questionText,
            questionTextIsValid: AskFormStore.isQuestionTextValid(),
            showDisclaimer,
            userName,
            userNameIsValid: AskFormStore.isUserNameValid(),
        };
    }

    onAgreeWithDisclaimer() {
        this.setState(({ agreedWithDisclaimer }) => ({ agreedWithDisclaimer: !agreedWithDisclaimer }));
    }

    onFormSubmit(event) {
        const { recaptchaValue } = this.state;
        const {
            configId,
            contactInfo,
            customTags,
            metadata,
            questionCategory,
            questionText,
            contactInfoWasPrefilled,
            userName,
        } = this.props;

        this.setState(
            () => ({ formWasSubmitted: true }),
            () => {
                if (this.formIsValid) {
                    ChatActions.askQuestion({
                        configId,
                        contactInfo,
                        customTags,
                        emailPrefilled: contactInfoWasPrefilled,
                        metadata,
                        questionText: trim(questionText),
                        questionCategory,
                        recaptchaValue,
                        userName,
                    });
                } else {
                    this.focusFirstErroredInput();
                }
            }
        );
        event.preventDefault();
    }

    onRecaptchaChange(value) {
        this.setState(() => ({ recaptchaValue: value }));
    }

    focusFirstErroredInput() {
        const erroredInputs = filter(this.inputRefs, input => {
            if (input.id === "name") return !this.props.userNameIsValid;
            if (input.id === "email") return !this.props.contactInfoIsValid;
            if (input.id === "question") return !this.props.questionTextIsValid;

            return false;
        });

        invoke(erroredInputs, "[0].focus");
    }

    focusFirstInput() {
        invoke(this.inputRefs, "[0].focus");
    }

    get askFormHeight() {
        /*
         * TODO: get rid of these magic numbers and compute height from actual DOM elements
         */
        const wrapperPadding = isMobileDevice() ? 0 : 40;
        const HEADER_HEIGHT = 74;

        return `${get(this.questionFormRef, "current.offsetHeight", 0) + HEADER_HEIGHT + wrapperPadding}px`;
    }

    preventScrollingOutsideOfTextarea(event) {
        const scrollingThroughTextarea = isNodeInRoot(event.target, this.questionInputRef.current);

        if (!scrollingThroughTextarea) event.preventDefault();
    }

    setInputRef(ref) {
        this.inputRefs.push(ref);
    }

    get formIsValid() {
        return (
            this.props.contactInfoIsValid &&
            this.props.userNameIsValid &&
            this.props.questionTextIsValid &&
            this.recaptchaIsValid
        );
    }

    get recaptchaIsValid() {
        return !window.companySettings.forceRecaptcha || Boolean(this.state.recaptchaValue);
    }

    render() {
        const { allowQuestionSignupWithPhoneNumber, forceRecaptcha, name: companyName } = window.companySettings;
        const { agreedWithDisclaimer, formWasSubmitted, recaptchaValue } = this.state;
        const {
            error,
            isProcessing,
            labels,
            lastResponseTimeInMillis,
            showDisclaimer,
        } = this.props;
        const shouldDisableSubmitButton = (showDisclaimer && !agreedWithDisclaimer) || isProcessing;
        const contactInfoLabel = allowQuestionSignupWithPhoneNumber
            ? i18n.t("rtm.label.email_or_phone")
            : i18n.t("rtm.label.email");

        return (
            <div className="ask-question-form-container">
                <form onSubmit={this.onFormSubmit} noValidate ref={this.questionFormRef} className="ask-question-form">
                    <AskQuestionFormNameInput
                        askFormHeight={this.askFormHeight}
                        disabled={isProcessing}
                        shouldDisplayError={formWasSubmitted}
                        setRef={this.setInputRef}
                    />

                    <AskQuestionFormContactInfoInput
                        allowQuestionSignupWithPhoneNumber={allowQuestionSignupWithPhoneNumber}
                        askFormHeight={this.askFormHeight}
                        disabled={isProcessing}
                        label={contactInfoLabel}
                        shouldDisplayError={formWasSubmitted}
                        setRef={this.setInputRef}
                    />

                    <AskQuestionFormQuestionInput
                        askFormHeight={this.askFormHeight}
                        disabled={isProcessing}
                        isVisible
                        shouldDisplayError={formWasSubmitted}
                        setRef={this.setInputRef}
                    />

                    {forceRecaptcha && (
                        <div className="input-group rc-input-group">
                            <ReCAPTCHA
                                sitekey={window.recaptchaSiteKey}
                                onChange={this.onRecaptchaChange}
                                size="normal"
                            />
                            {!recaptchaValue && formWasSubmitted && (
                                <span className="error">{i18n.t("rtm.error.please_solve_the_captcha")}</span>
                            )}
                        </div>
                    )}

                    {showDisclaimer && (
                        <div className="ask-question-disclaimer-confirmation">
                            <input
                                checked={agreedWithDisclaimer}
                                id="disclaimerAgree"
                                onChange={this.onAgreeWithDisclaimer}
                                type="checkbox"
                            />
                            <label htmlFor="disclaimerAgree">{i18n.t("rtm.text.agree_terms_below")}</label>
                        </div>
                    )}

                    <div className="submit-group">
                        <AskQuestionFormButton
                            ref={this.questionButtonRef}
                            isDisabled={shouldDisableSubmitButton}
                            label={labels.askButton}
                        />
                    </div>

                    {showDisclaimer && (
                        <p
                            className="ask-question-disclaimer"
                            dangerouslySetInnerHTML={{
                                __html: i18n.t("rtm.text.ask_question_disclaimer", { companyName }),
                            }}
                        />
                    )}

                    <div className="submit-group">
                        {lastResponseTimeInMillis && (
                            <p
                                className="expert-response-time"
                                dangerouslySetInnerHTML={{
                                    __html: i18n.t("rtm.text.response_time", {
                                        companyName:
                                            i18next.language === "en" ? Articles.articlize(companyName) : companyName,
                                        lastResponseTime: moment.duration(lastResponseTimeInMillis).humanize(),
                                    }),
                                }}
                            />
                        )}
                        {error && <span className="error">{i18n.t("rtm.error.processing_request")}</span>}
                    </div>
                </form>
            </div>
        );
    }
}

AskQuestionForm.defaultProps = {
    contactInfo: "",
    customTags: [],
    error: null,
    lastResponseTimeInMillis: null,
    metadata: {},
    questionCategory: "",
    questionText: "",
    userName: "",
};

AskQuestionForm.propTypes = {
    configId: PropTypes.string.isRequired,
    contactInfo: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    contactInfoIsValid: PropTypes.bool.isRequired,
    contactInfoWasPrefilled: PropTypes.bool.isRequired,
    customTags: PropTypes.arrayOf(PropTypes.string),
    error: PropTypes.shape({
        crossDomain: PropTypes.bool.isRequired,
        message: PropTypes.string.isRequired,
        method: PropTypes.string.isRequired,
        stack: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
    }),
    isLoading: PropTypes.bool.isRequired,
    isProcessing: PropTypes.bool.isRequired,
    labels: PropTypes.shape({
        askBubble: PropTypes.string.isRequired,
        askButton: PropTypes.string.isRequired,
        askFormHeader: PropTypes.string.isRequired,
    }).isRequired,
    lastResponseTimeInMillis: PropTypes.number,
    metadata: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    questionText: PropTypes.string,
    questionTextIsValid: PropTypes.bool.isRequired,
    questionCategory: PropTypes.string,
    showDisclaimer: PropTypes.bool.isRequired,
    userName: PropTypes.string,
    userNameIsValid: PropTypes.bool.isRequired,
};

export default connectToStores(AskQuestionForm);
